import { useState } from "react";
import { toast } from "sonner";
import { Mail, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errorMessage, useApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtRelative, ROLE_LABELS } from "@/lib/format";
import type { User } from "@/lib/types";
import { Loading, PageHeader } from "@/components/app/PageHeader";

export function randomPassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  const buf = new Uint32Array(12);
  crypto.getRandomValues(buf);
  return Array.from(buf.slice(0, 10), (n) => chars[n % chars.length]).join("") + "!@#$%*"[buf[10] % 6] + (buf[11] % 10);
}

export function UserForm({ onSubmit, testPrefix }: { onSubmit: (f: any) => Promise<void>; testPrefix: string }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "editor" });
  const [invite, setInvite] = useState(false);
  const valid = form.email && form.name && (invite || !form.password || form.password.length >= 8);
  const submit = () => onSubmit(invite ? { email: form.email, name: form.name, role: form.role, invite: true } : { email: form.email, name: form.name, role: form.role, ...(form.password ? { password: form.password } : {}) });
  return (
    <div className="space-y-4">
      <div className="space-y-2"><Label>Nom</Label><Input data-testid={`${testPrefix}-name`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-slate-700 bg-slate-950/60" /></div>
      <div className="space-y-2"><Label>Courriel</Label><Input type="email" data-testid={`${testPrefix}-email`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-slate-700 bg-slate-950/60" /></div>
      <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-3">
        <div><p className="text-sm font-medium text-slate-200">Envoyer plutôt un lien d'invitation</p><p className="text-xs text-slate-500">Par défaut, un courriel de bienvenue avec le nom d'utilisateur et le mot de passe est envoyé.</p></div>
        <Switch data-testid={`${testPrefix}-invite-switch`} checked={invite} onCheckedChange={setInvite} />
      </div>
      {!invite && (
        <div className="space-y-2">
          <Label>Mot de passe</Label>
          <div className="flex gap-2">
            <Input type="text" autoComplete="off" data-testid={`${testPrefix}-password`} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Laisser vide pour générer automatiquement" className="font-mono border-slate-700 bg-slate-950/60" />
            <Button type="button" variant="outline" data-testid={`${testPrefix}-generate-password`} onClick={() => setForm({ ...form, password: randomPassword() })} className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700">Générer</Button>
          </div>
          <p className="text-xs text-slate-500">8 caractères minimum. Il sera inclus dans le courriel de bienvenue.</p>
        </div>
      )}
      <div className="space-y-2"><Label>Rôle</Label>
        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
          <SelectTrigger data-testid={`${testPrefix}-role`} className="border-slate-700 bg-slate-950/60"><SelectValue /></SelectTrigger>
          <SelectContent className="border-slate-800 bg-slate-900"><SelectItem value="organization_admin">Administrateur (tout dans l'organisation)</SelectItem><SelectItem value="editor">Éditeur (médias et listes)</SelectItem></SelectContent>
        </Select>
      </div>
      <Button data-testid={`${testPrefix}-submit`} disabled={!valid} onClick={submit} className="w-full bg-blue-600 text-white hover:bg-blue-500">{invite ? "Créer et envoyer l'invitation" : "Créer et envoyer les identifiants"}</Button>
    </div>
  );
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const { data, loading, reload } = useApi<User[]>("/users");
  const [open, setOpen] = useState(false);

  const create = async (form: any) => {
    try {
      const { data } = await api.post("/users", form);
      toast.success(data.invite_pending ? `Invitation envoyée à ${data.email}` : `Compte créé — identifiants envoyés à ${data.email}`);
      setOpen(false); reload();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const update = async (u: User, patch: Partial<User>) => {
    try { await api.patch(`/users/${u.id}`, patch); toast.success("Utilisateur mis à jour"); reload(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const resend = async (u: User) => {
    try { await api.post(`/users/${u.id}/invite`); toast.success(`Invitation renvoyée à ${u.email}`); } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Accès" title="Utilisateurs de l'organisation" description="Les éditeurs gèrent uniquement médias et listes ; les administrateurs gèrent tout."
        actions={<Button data-testid="btn-create-user" onClick={() => setOpen(true)} className="bg-blue-600 text-white hover:bg-blue-500"><UserPlus className="mr-1 h-4 w-4" />Ajouter</Button>} />
      {loading && !data ? <Loading /> : (
        <div className="panel overflow-x-auto">
          <Table data-testid="users-table">
            <TableHeader><TableRow className="border-slate-800 hover:bg-transparent"><TableHead>Nom</TableHead><TableHead>Courriel</TableHead><TableHead>Rôle</TableHead><TableHead>Dernière connexion</TableHead><TableHead>Actif</TableHead></TableRow></TableHeader>
            <TableBody>
              {(data || []).map((u) => {
                const self = me && me.id === u.id;
                return (
                  <TableRow key={u.id} className="border-slate-800 hover:bg-slate-800/30">
                    <TableCell className="font-medium text-slate-100">{u.name}{self && <span className="ml-2 text-[10px] text-cyan-400">(vous)</span>}</TableCell>
                    <TableCell className="text-slate-400">{u.email}</TableCell>
                    <TableCell>
                      <Select value={u.role} disabled={!!self} onValueChange={(v) => update(u, { role: v as any })}>
                        <SelectTrigger data-testid={`user-role-select-${u.id}`} className="h-8 w-44 border-slate-700 bg-slate-950/60"><SelectValue>{ROLE_LABELS[u.role]}</SelectValue></SelectTrigger>
                        <SelectContent className="border-slate-800 bg-slate-900"><SelectItem value="organization_admin">Administrateur</SelectItem><SelectItem value="editor">Éditeur</SelectItem></SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-slate-400">{u.invite_pending ? (
                      <span className="flex items-center gap-2"><span data-testid={`user-invite-pending-${u.id}`} className="rounded-full border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-400">Invitation en attente</span>
                        <Button variant="ghost" size="sm" data-testid={`user-resend-invite-${u.id}`} onClick={() => resend(u)} className="h-7 px-2 text-xs text-blue-400 hover:text-blue-300"><Mail className="mr-1 h-3 w-3" />Renvoyer</Button></span>
                    ) : fmtRelative(u.last_login_at)}</TableCell>
                    <TableCell><Switch data-testid={`user-active-switch-${u.id}`} disabled={!!self} checked={u.active} onCheckedChange={(c) => update(u, { active: c })} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-slate-800 bg-slate-900"><DialogHeader><DialogTitle>Nouvel utilisateur</DialogTitle></DialogHeader><UserForm onSubmit={create} testPrefix="user-form" /></DialogContent>
      </Dialog>
    </div>
  );
}
